import React, { useEffect, useRef, useState } from 'react';
import styles from './MainPage.module.scss';
import { useNavigate } from 'react-router-dom';
import { AppContainer } from '@/components/AppContainer/AppContainer';
import { countFormatter, countTotalFormatter } from '@/utils/countFormatter';
import { ReactComponent as StarIcon } from '@/assets/Icons/star.svg';
import { ReactComponent as NoteTextIcon } from '@/assets/Icons/note-text.svg';
import { ReactComponent as MoneyWalletIcon } from '@/assets/Icons/money-withdrawal.svg';
import CharacterIcon from '@/assets/ch.png';
import { getBalance } from '@/api/modules/User/balance';
import { getAllEarn } from '@/api/modules/User/all_balance';
import { getTasksCompleted } from '@/api/modules/User/tasks_completed';
import WalletModal from '@/components/WalletModal/WalletModal';
import WebApp from '@twa-dev/sdk';
import { onbPass } from '@/api/modules/User/onb';
import { Loader } from '@/components/Loader/Loader';
import WheelButton from '../../../public/wheelPic.webp';
import SafeButton from '../../../public/safePic.webp';
import VoucherButton from '../../../public/voucherlogo.webp';
import WheelModal from '@/components/WheelModal';
import useBonusStore from '@/store/BonusStore';
import { getCaseUpdateTime } from '@/api/modules/Case/case';
import { TimeDisplay } from '@/utils/TimeDisplay';
import VoucherModal from '@/components/VoucherModal/VoucherModal';

const ANIMATION_DURATION = 1500;

interface MainPageProps {
  onWalletOpen: () => void;
}

const MainPage: React.FC<MainPageProps> = ({ onWalletOpen }) => {
  const tg = window?.Telegram?.WebApp;
  const navigate = useNavigate();
  const characterRef = useRef<HTMLDivElement>(null);
  const totalRef = useRef<HTMLDivElement>(null);
  const [total, setTotal] = useState(0);
  const [balance, setBalance] = useState(0);
  const [tasksCompleted, setTasksCompleted] = useState(0);

  // Анимированные значения
  const [animatedTotal, setAnimatedTotal] = useState(0);
  const [animatedBalance, setAnimatedBalance] = useState(0);
  const isAnimatedRef = useRef(false);
  const prevBalanceRef = useRef<number | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [wheelModalOpen, setWheelModalOpen] = useState(false);
  const bonus = useBonusStore((state) => state.bonus);
  const [caseUpdateTime, setCaseUpdateTime] = useState('');
  useEffect(() => {
    const fetchCaseUpdateTime = async () => {
      try {
        const response = await getCaseUpdateTime();
        console.log(response, 'LALALAL');
        setCaseUpdateTime(response.next_midnight);
      } catch (error) {
        console.error('Ошибка при получении времени обновления сейфа:', error);
        setCaseUpdateTime('');
      }
    };
    fetchCaseUpdateTime();
  }, []);
  const fetchBalance = async () => {
    try {
      const response = await getBalance();
      if (response?.data?.amount !== undefined) {
        setBalance(response.data.amount); // это локальный стейт
      } else {
        setBalance(0);
      }
    } catch (error) {
      console.error('Ошибка при получении баланса:', error);
      setBalance(0);
    }
  };
  useEffect(() => {
    if (WebApp) {
      WebApp.ready();
    }

    // Загружаем изображения
    const loadImages = () => {
      const images = [
        CharacterIcon,
        '/clickStatic.png',
        WheelButton,
        SafeButton,
        VoucherButton,
      ];

      let loadedCount = 0;
      const totalImages = images.length;

      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };

      images.forEach((src) => {
        const img = new Image();
        img.onload = checkAllLoaded;
        img.onerror = checkAllLoaded; // Продолжаем даже если изображение не загрузилось
        img.src = src;
      });
    };

    loadImages();

    getTasksCompleted()
      .then((response) => {
        console.log('response:', response.data.count_tasks);
        setTasksCompleted(response.data.count_tasks);
      })
      .catch((error) => {
        console.error('Ошибка при получении задач:', error);
      });
    getAllEarn()
      .then((response) => {
        console.log('response:', response.data.earnings_sum);
        setTotal(response.data.earnings_sum);
      })
      .catch((error) => {
        console.error('Ошибка при получении баланса:', error);
      });

    fetchBalance();
  }, []);

  useEffect(() => {
    const handleBonusReceived = () => {
      fetchBalance();
    };

    window.addEventListener('bonus-received', handleBonusReceived);

    return () => {
      window.removeEventListener('bonus-received', handleBonusReceived);
    };
  }, []);

  // Анимация общего заработка
  useEffect(() => {
    // Запускаем анимацию только после загрузки изображений
    if (!imagesLoaded) return;

    let start = 0;
    let startTime: number | null = null;
    let rafId: number;
    if (total > 0) {
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min(
          (timestamp - startTime) / ANIMATION_DURATION,
          1,
        );
        const value = Math.floor(progress * total * 100) / 100;
        setAnimatedTotal(value);
        if (progress < 1) {
          rafId = requestAnimationFrame(animate);
        } else {
          setAnimatedTotal(total);
        }
      };
      setAnimatedTotal(0);
      rafId = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(rafId);
    } else {
      setAnimatedTotal(total);
    }
  }, [total, imagesLoaded, bonus]);

  // Анимация баланса только при первом входе или изменении значения баланса
  useEffect(() => {
    // Запускаем анимацию только после загрузки изображений
    if (!imagesLoaded) return;
    if (bonus) {
      return;
    }
    // Если баланс не изменился, не анимируем
    if (prevBalanceRef.current === balance && isAnimatedRef.current) {
      setAnimatedBalance(balance);
      return;
    }
    let startTime: number | null = null;
    let rafId: number;
    isAnimatedRef.current = true;
    prevBalanceRef.current = balance;
    if (balance > 0) {
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min(
          (timestamp - startTime) / ANIMATION_DURATION,
          1,
        );
        const value = Math.floor(progress * balance * 1000) / 1000;
        setAnimatedBalance(value);
        if (progress < 1) {
          rafId = requestAnimationFrame(animate);
        } else {
          setAnimatedBalance(balance);
        }
      };
      setAnimatedBalance(0);
      rafId = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(rafId);
    } else {
      setAnimatedBalance(balance);
    }
  }, [balance, imagesLoaded, bonus]);

  const handleCardClick = () => {
    // Используем нативную навигацию для ускорения
    navigate('/tasks');
  };

  const handleWheelModalOpen = () => {
    navigate('/wheel');
  };
  const handleSafeModalOpen = () => {
    navigate('/case');
  };
  const handleWheelModalClose = () => setWheelModalOpen(false);

  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const handleVoucherModalOpen = () => {
    navigate('/voucher');
  };
  const handleVoucherModalClose = () => {
    setVoucherModalOpen(false);
  };

  if (!imagesLoaded) {
    return (
      <AppContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Loader />
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <div className={styles.mainPage}>
        <div className={styles.mainPage__total} ref={totalRef}>
          <div className={styles.wrapperWithStar}>
            <h1>{countTotalFormatter(animatedTotal, 'int')}</h1>
            <StarIcon
              style={{
                width: 24,
                height: 24,
              }}
            />
          </div>

          <div className={styles.wrapperWithStar}>
            <p>Игроки заработали</p>
          </div>
        </div>

        <div className={styles.mainPage__card}>
          <div className={styles.mainPage__character} ref={characterRef}>
            <img src={CharacterIcon} alt="character" />
          </div>
          <div
            className={styles.orangeNotification}
            onClick={handleWheelModalOpen}
          >
            <div className={styles.redDot}></div>
            <img src={WheelButton} alt="wheel" />
          </div>
          <div
            className={styles.safeNotification}
            onClick={handleSafeModalOpen}
          >
            <div className={styles.redDot}></div>
            <img src={SafeButton} alt="safe" />
            <div className={styles.caseUpdateTime}>
              <TimeDisplay endDate={caseUpdateTime || ''} />
            </div>
          </div>
          <div
            className={styles.voucherNotification}
            onClick={handleVoucherModalOpen}
          >
            <div className={styles.redDot}></div>
            <img src={VoucherButton} alt="safe" />
          </div>
          <div className={styles.mainPage__card_content}>
            <div className={styles.card__row}>
              <div className={styles.card__column_1}>
                <span
                  className={`${styles.card__label} ${styles['card__label--balance']}`}
                >
                  Баланс
                </span>
              </div>
              <div className={styles.card__column_2}>
                <span className={styles.card__value_star}>
                  <StarIcon className={styles.card__icon} />
                  {countFormatter(animatedBalance)}
                </span>
              </div>
              <div className={styles.card__column_3}>
                <button className={styles.card__copyBtn} onClick={onWalletOpen}>
                  <MoneyWalletIcon />
                </button>
              </div>
            </div>
            <div className={styles.card__divider} />
            <div
              className={`${styles.card__row} ${styles.card__row_completed}`}
            >
              <div className={styles.card__column_1}>
                <span className={styles.card__label}>
                  Выполненные <br /> задания
                </span>
              </div>
              <div className={styles.card__column_2}>
                <span className={styles.card__value}>
                  <NoteTextIcon className={styles.card__icon} />
                  {tasksCompleted}
                </span>
              </div>
              <div className={styles.card__column_3}></div>
            </div>
            <div
              className={`${styles.card__row} ${styles.card__row_task}`}
              onClick={handleCardClick}
            >
              <div className={styles.card__taskButton}>
                <span
                  className={`${styles.card__label} ${styles.card__label_task}`}
                >
                  Перейти к заданиям
                </span>
                <img
                  src="/cursor-click.png"
                  alt="click animation"
                  className={styles.clickGif}
                />
              </div>
            </div>
          </div>
        </div>
        {/* {walletModal} */}
      </div>
      <WheelModal open={wheelModalOpen} onClose={handleWheelModalClose} />
      <VoucherModal open={voucherModalOpen} onClose={handleVoucherModalClose} />
    </AppContainer>
  );
};

export default MainPage;
